import useHelpers from 'hooks/helpers';
import { useCallback, useMemo } from 'react';
import { useI18n } from 'next-localization';
import useWindowDimensions from 'hooks/useWindowDimensions';
import { standardFormat } from '@/constants/dateFormats';

interface Columns {
  columnDisplayOrder: number;
  columnLabel: string;
  columnRowValue: string;
  columnRowValueType: string;
  invariantColumnLabel: string;
}

interface ResultsItem {
  columns: Array<Columns>;
  rowDisplayOrder: number;
  rowLabel: string;
}

const useStripedTableHelper = (data: any, isLoading: boolean) => {
  const { t } = useI18n();
  const { formatNumber, localizeDate } = useHelpers();
  const deviceType: string = useWindowDimensions();

  const tableColumnHeadings = useMemo(() => {
    if (!isLoading) {
      const rowsColumnData: Array<{ label: string; order: number }> = [];

      data?.results.forEach((item) => {
        item.columns.forEach((col: Columns) => {
          rowsColumnData.push({ label: col.columnLabel, order: col.columnDisplayOrder });
        });
      });

      rowsColumnData.sort((a, b) => a.order - b.order);

      return new Set(rowsColumnData.map((col) => col.label));
    }
  }, [data, isLoading]);

  const reportDate = useMemo(() => {
    return (
      t('fundDetailsHoldingsReportDate') + ' ' + localizeDate(data?.reportDate, standardFormat)
    );
  }, [t, data, localizeDate]);

  const reportDateFromTo = useMemo(() => {
    return `${t('fundDetailsHoldingsReportDate')} ${localizeDate(
      data?.reportPeriodFromDate,
      standardFormat
    )} - ${localizeDate(data?.reportDate, standardFormat)}`;
  }, [t, data, localizeDate]);

  const applyGeneralFormattingRules = (col) => {
    if (col.columnRowValue === '') {
      return t('notAvailableShortMessage');
    } else if (col.columnRowValueType === 'Percentage') {
      return `${formatNumber(+col.columnRowValue)}%`;
    } else if (col.columnRowValueType === 'Decimal') {
      return formatNumber(+col.columnRowValue);
    } else if (col.columnRowValueType === 'Text' || col.columnRowValueType === 'Integer') {
      return col.columnRowValue;
    } else {
      return col.columnRowValue;
    }
  };

  const formatTableValues = useCallback(
    (columns: Array<Columns>) => {
      return Array.from(tableColumnHeadings).map((heading) => {
        const col = columns.find((col) => col.columnLabel === heading);
        if (!col) return t('notAvailableShortMessage');

        return applyGeneralFormattingRules(col);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableColumnHeadings, t]
  );

  const tableData = useMemo(() => {
    if (isLoading) {
      return {
        tableHeaderLabels: [],
        tableBodyRowLabels: [
          {
            rowLabel: '',
            values: '',
          },
        ],
      };
    }

    return {
      tableHeaderLabels: tableColumnHeadings,
      tableBodyRowLabels: data?.results.map((item: ResultsItem) => {
        return {
          rowLabel: item.rowLabel,
          values: formatTableValues(item.columns),
        };
      }),
    };
  }, [tableColumnHeadings, isLoading, formatTableValues, data?.results]);

  const isScrollable = useMemo(() => {
    if (data === null || isLoading) return false;
    if (deviceType === 'mobile' || deviceType === 'tablet') {
      return tableColumnHeadings.size >= 3;
    } else {
      return tableColumnHeadings.size > 4;
    }
  }, [tableColumnHeadings, deviceType, data, isLoading]);

  return { tableData, isScrollable, reportDate, reportDateFromTo };
};

export default useStripedTableHelper;
